import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Component as ComponentIcon } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { AdminHeader } from '@components/layout/admin-header'
import { useListCache } from '@global/hooks/queries/useListCache'
import { ListTableContainer } from './listTableContainer'
import { EditToolbar } from './toolbar'
import { useListObserver } from './useListObserver'
import { NewColumnModal } from '../components/new-column-modal'
import { Trans } from '@lingui/react/macro'

interface HeaderProps {
  name: string
  type: string
  isLoading: boolean
}

const Header = ({ name, type, isLoading }: HeaderProps) => (
  <AdminHeader>
    <AdminHeader.PageIcon>
      <ComponentIcon color="hsla(194, 47%, 20%, 1)" size={28} />
    </AdminHeader.PageIcon>
    <div className="ml-4">
      <AdminHeader.ManagerTitle>
        <Trans>List Manager</Trans>
      </AdminHeader.ManagerTitle>
      {isLoading ? (
        <div className="flex items-center gap-3 h-[28px]">
          <div className="bg-cv-blue-200 w-12 h-4 rounded-md my-2" />
          <div className="bg-cv-blue-200 w-[100px] h-4 rounded-md my-2" />
        </div>
      ) : (
        <AdminHeader.Title>{name}</AdminHeader.Title>
      )}
    </div>
    <div className="flex items-center ml-4">
      {!isLoading ? <AdminHeader.Pill>{type}</AdminHeader.Pill> : null}
    </div>
  </AdminHeader>
)

interface ListsManagerEditRouterParams {
  id: string
}

export const ListsManagerEdit = () => {
  const params = useParams<ListsManagerEditRouterParams>()
  const { data: list, isLoading } = useListObserver({ listId: params.id })
  const { insertColumn } = useListCache({ listId: params.id })

  const [isNewColumnModalOpen, setIsNewColumnModalOpen] = useState(false)

  const handleInsertColumn = useCallback(
    ({ name, addAnother = false }: { name: string; addAnother?: boolean }) => {
      insertColumn({
        newColumnName: name,
        selection: [{ start: { col: list?.list_columns.length ?? 0 } }],
      })
      setIsNewColumnModalOpen(addAnother)
    },
    [list],
  )

  return (
    <Page>
      <Header name={list?.name ?? ''} type={list?.type ?? ''} isLoading={isLoading} />
      <EditToolbar listId={params.id} />
      <ListTableContainer listId={params.id} setIsNewColumnModalOpen={setIsNewColumnModalOpen} />

      <NewColumnModal
        isOpen={isNewColumnModalOpen}
        setIsOpen={setIsNewColumnModalOpen}
        insertColumn={handleInsertColumn}
      />
    </Page>
  )
}

interface PageProps {
  children: React.ReactNode
}

function Page({ children }: PageProps) {
  return (
    <motion.div
      className="edit-list h-full bg-white overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      {children}
    </motion.div>
  )
}
